<template>
    <div class="bsa-toolbar">
        <div class="bsa-toolbar-bar">
            <div class="bsa-toolbar-filters">
                <div class="bsa-toolbar-field">
                    <label class="bsa-toolbar-label">From</label>
                    <input v-model="start" type="date" class="form-control bsa-toolbar-date" @change="updateDateQuery">
                </div>
                <div class="bsa-toolbar-field">
                    <label class="bsa-toolbar-label">To</label>
                    <input v-model="end" type="date" class="form-control bsa-toolbar-date" @change="updateDateQuery">
                </div>
                <div class="bsa-toolbar-field">
                    <label class="bsa-toolbar-label">Column By</label>
                    <div class="bsa-pills" role="group">
                        <button
                            v-for="option in columnByOptions"
                            :key="option.key"
                            type="button"
                            class="bsa-pill"
                            :class="{ 'is-active': columnBy === option.key }"
                            @click="emit('update:columnBy', option.key)"
                        >{{ option.name }}</button>
                    </div>
                </div>
                <div v-if="columnBy === 'period'" class="bsa-toolbar-field">
                    <label class="bsa-toolbar-label">Period</label>
                    <div class="bsa-pills" role="group">
                        <button
                            v-for="option in periods"
                            :key="option.key"
                            type="button"
                            class="bsa-pill"
                            :class="{ 'is-active': period === option.key }"
                            @click="emit('update:period', option.key)"
                        >{{ option.name }}</button>
                    </div>
                </div>
                <button type="button" class="btn btn-primary bsa-toolbar-go" @click="onClickGo">Go</button>
            </div>
            <div class="bsa-toolbar-actions">
                <button type="button" class="btn bsa-toolbar-btn" @click="emit('export')">
                    <i class="fa-regular fa-file-excel"></i>
                    Export
                </button>
                <FilterPopover
                    title="Filters"
                    :active-count="chips.length"
                    popover-width="460px"
                    @open="onOpenFilter"
                    @apply="onApplyFilter"
                    @clear="emit('clear')"
                >
                    <div class="col-12 col-sm-6">
                        <label class="filter-label">Business</label>
                        <v-select
                            v-model="draft.business_id"
                            placeholder="Any business"
                            :options="businesses"
                            label="name"
                            :reduce="option => option.id"
                        />
                    </div>
                    <div class="col-12 col-sm-6">
                        <label class="filter-label">Project</label>
                        <v-select
                            v-model="draft.project_id"
                            placeholder="Any project"
                            :options="projects"
                            label="name"
                            :reduce="option => option.id"
                        />
                    </div>
                </FilterPopover>
            </div>
        </div>
        <div v-if="chips.length" class="bsa-chips">
            <span v-for="chip in chips" :key="chip.key" class="bsa-chip">
                <span class="bsa-chip-label">{{ chip.label }}:</span>
                {{ chip.text }}
                <button type="button" class="bsa-chip-remove" @click="emit('remove-chip', chip.key)">
                    <i class="fa-solid fa-xmark"></i>
                </button>
            </span>
            <button type="button" class="bsa-chip-clear" @click="emit('clear')">Clear all</button>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import FilterPopover from '@/components/atom/FilterPopover.vue'

const props = defineProps({
    columnBy: { type: String, default: 'period' },
    period: { type: String, default: 'monthly' },
    periods: { type: Array, default: () => [] },
    businesses: { type: Array, default: () => [] },
    projects: { type: Array, default: () => [] },
    businessId: { type: [Number, String], default: null },
    projectId: { type: [Number, String], default: null }
})

const emit = defineEmits(['update:columnBy', 'update:period', 'go', 'apply', 'remove-chip', 'clear', 'export'])

const $route = useRoute()
const $router = useRouter()

const columnByOptions = [
    { name: 'Period', key: 'period' },
    { name: 'Business Unit', key: 'business' },
    { name: 'Project', key: 'project' }
]

const start = ref($route.query.start || '')
const end = ref($route.query.end || '')
const draft = ref({ business_id: null, project_id: null })

const optionName = (options, id) => {
    const option = options.find(item => String(item.id) === String(id))
    return option ? option.name : `#${id}`
}

const chips = computed(() => {
    const list = []
    if (props.businessId) list.push({ key: 'business_id', label: 'Business', text: optionName(props.businesses, props.businessId) })
    if (props.projectId) list.push({ key: 'project_id', label: 'Project', text: optionName(props.projects, props.projectId) })
    return list
})

const buildDateQuery = () => {
    const routeQuery = Object.assign({}, $route.query, { start: start.value, end: end.value })
    if (!start.value) delete routeQuery.start
    if (!end.value) delete routeQuery.end
    return routeQuery
}

const updateDateQuery = async () => {
    await $router.push({ path: $route.path, query: buildDateQuery() })
}

const onClickGo = async () => {
    await updateDateQuery()
    emit('go')
}

const onOpenFilter = () => {
    draft.value = { business_id: props.businessId, project_id: props.projectId }
}

const onApplyFilter = () => {
    emit('apply', Object.assign({}, draft.value))
}
</script>

<style scoped>
.bsa-toolbar {
    margin-bottom: 16px;
}
.bsa-toolbar-bar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
}
.bsa-toolbar-filters {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
}
.bsa-toolbar-field {
    display: flex;
    flex-direction: column;
}
.bsa-toolbar-label {
    display: block;
    margin-bottom: 4px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #101828;
}
.bsa-toolbar-date {
    width: 160px;
    height: 38px;
    border-radius: 8px;
    border-color: #e4e7ec;
}
.bsa-pills {
    display: inline-flex;
    height: 38px;
    padding: 3px;
    border-radius: 8px;
    background: #f2f4f7;
}
.bsa-pill {
    padding: 0 12px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    font-size: 13px;
    font-weight: 500;
    color: #667085;
    white-space: nowrap;
    cursor: pointer;
}
.bsa-pill:hover {
    color: #344054;
}
.bsa-pill.is-active {
    background: #fff;
    color: #7367f0;
    box-shadow: 0 1px 2px rgba(16, 24, 40, .08);
}
.bsa-toolbar-go {
    min-width: 72px;
    height: 38px;
    border-radius: 8px;
}
.bsa-toolbar-actions {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    margin-left: auto;
}
.bsa-toolbar-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 38px;
    padding: 0 14px;
    border: 1px solid #e4e7ec;
    border-radius: 10px;
    background: #fff;
    color: #475467;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
}
.bsa-toolbar-btn:hover {
    background: #f9fafb;
    color: #101828;
}
.bsa-chips {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
}
.bsa-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 8px 4px 10px;
    border-radius: 16px;
    background: #f4f3ff;
    color: #4338ca;
    font-size: 12px;
}
.bsa-chip-label {
    color: #7c78a8;
}
.bsa-chip-remove {
    border: 0;
    background: transparent;
    color: #7c78a8;
    font-size: 11px;
    line-height: 1;
    padding: 2px;
    cursor: pointer;
}
.bsa-chip-remove:hover {
    color: #4338ca;
}
.bsa-chip-clear {
    border: 0;
    background: transparent;
    color: #667085;
    font-size: 12px;
    text-decoration: underline;
    cursor: pointer;
}
@media (max-width: 576px) {
    .bsa-toolbar-filters {
        width: 100%;
    }
    .bsa-toolbar-field {
        flex: 1 1 140px;
    }
    .bsa-toolbar-date {
        width: 100%;
    }
    .bsa-pills {
        overflow-x: auto;
    }
}
</style>
